import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PageTitle from "../components/PageTitle";
import useIsSuperUser from "../../shared/hooks/useIsSuperUser";
import { api, ClusterStatus } from "../../shared/api/client";

type UserInfo = {
  id: string;
  username: string;
  displayName?: string;
  avatarUrl?: string;
  role?: string;
  canCreateGames?: boolean;
};

export default function AdminPanel() {
  const isSuperUser = useIsSuperUser();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<UserInfo[]>([]);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [clusterStatus, setClusterStatus] = useState<ClusterStatus | null>(null);
  const [clusterLoading, setClusterLoading] = useState(false);
  const [clusterError, setClusterError] = useState<string | null>(null);

  const fetchClusterStatus = useCallback(async () => {
    setClusterLoading(true);
    setClusterError(null);
    try {
      const status = await api.getClusterStatus();
      setClusterStatus(status);
    } catch (err: unknown) {
      setClusterError(err instanceof Error ? err.message : "Failed to fetch cluster status");
    } finally {
      setClusterLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClusterStatus();
    const interval = setInterval(fetchClusterStatus, 15000);
    return () => clearInterval(interval);
  }, [fetchClusterStatus]);

  const searchUsers = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError(null);
    setSuccess(null);
    setSearchResults([]);
    setUserInfo(null);

    try {
      const { users } = await api.searchUsers(searchQuery.trim());
      setSearchResults(users as UserInfo[]);
      if (users.length === 0) {
        setError("No users found matching that search");
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to search users");
    } finally {
      setLoading(false);
    }
  };

  const selectUser = (user: UserInfo) => {
    setUserInfo(user);
    setSearchResults([]);
  };

  const refreshUser = async () => {
    if (!userInfo) return;
    try {
      const { user } = await api.getUser(userInfo.id);
      setUserInfo(user as UserInfo);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to refresh user");
    }
  };

  const toggleSuperadmin = async (grant: boolean) => {
    if (!userInfo) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await api.updateUserPermissions(userInfo.id, {
        role: grant ? "superuser" : null,
      });
      setSuccess(
        grant
          ? `Granted superadmin role to ${userInfo.displayName || userInfo.id}`
          : `Removed superadmin role from ${userInfo.displayName || userInfo.id}`
      );
      // Refresh user info
      await refreshUser();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to update superadmin status");
    } finally {
      setLoading(false);
    }
  };

  const toggleCanCreateGames = async (grant: boolean) => {
    if (!userInfo) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await api.updateUserPermissions(userInfo.id, {
        canCreateGames: grant,
      });
      setSuccess(
        grant
          ? `Granted game creation permission to ${userInfo.displayName || userInfo.id}`
          : `Removed game creation permission from ${userInfo.displayName || userInfo.id}`
      );
      // Refresh user info
      await refreshUser();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to update game creation permission");
    } finally {
      setLoading(false);
    }
  };

  if (!isSuperUser) {
    return (
      <Box>
        <PageTitle title="Admin Panel" />
        <Alert severity="error">
          You do not have permission to access this page. Only superadmins can
          manage user permissions.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageTitle title="Admin Panel" />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">
              Cluster Status
            </Typography>
            <IconButton onClick={fetchClusterStatus} disabled={clusterLoading} size="small">
              <RefreshIcon />
            </IconButton>
          </Stack>

          {clusterLoading && !clusterStatus && <LinearProgress sx={{ mb: 2 }} />}

          {clusterError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {clusterError}
            </Alert>
          )}

          {clusterStatus && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <Chip
                  label={`${clusterStatus.cluster.serverCount} server${clusterStatus.cluster.serverCount !== 1 ? "s" : ""}`}
                  color="primary"
                  size="small"
                />
                <Chip
                  label={`${clusterStatus.locks.totalOwned} owned lock${clusterStatus.locks.totalOwned !== 1 ? "s" : ""}`}
                  size="small"
                />
                <Chip
                  label={`Epoch ${clusterStatus.locks.epoch}`}
                  size="small"
                  variant="outlined"
                />
              </Stack>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Server</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Processes</TableCell>
                      <TableCell align="right">Matches</TableCell>
                      <TableCell align="right">Connections</TableCell>
                      <TableCell>Heartbeat</TableCell>
                      <TableCell>Bundles</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clusterStatus.cluster.servers.map((server) => (
                      <TableRow
                        key={server.serverId}
                        sx={server.isThisServer ? { bgcolor: "action.selected" } : undefined}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                              {server.serverId.slice(0, 12)}
                            </Typography>
                            {server.isThisServer && (
                              <Chip label="this" size="small" variant="outlined" sx={{ height: 20 }} />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={server.status}
                            size="small"
                            color={server.status === "active" ? "success" : "warning"}
                            sx={{ height: 20 }}
                          />
                        </TableCell>
                        <TableCell align="right">{server.processCount}</TableCell>
                        <TableCell align="right">{server.matchCount}</TableCell>
                        <TableCell align="right">{server.connectionCount}</TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color={server.heartbeatAgeMs > 20000 ? "error" : "text.secondary"}
                          >
                            {Math.round(server.heartbeatAgeMs / 1000)}s ago
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {server.loadedBundles.length}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {clusterStatus.thisServer.ownedMatches.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Active Matches on This Server ({clusterStatus.thisServer.ownedMatches.length})
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {clusterStatus.thisServer.ownedMatches.map((matchId) => (
                      <Chip
                        key={matchId}
                        label={matchId.slice(0, 8)}
                        size="small"
                        variant="outlined"
                        sx={{ fontFamily: "monospace" }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>

      {clusterStatus && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Deno Processes (This Server)
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Chip
                  label={`Memory: ${clusterStatus.thisServer.denoProcesses.totalMemoryMb.toFixed(1)} / ${clusterStatus.thisServer.denoProcesses.totalMemoryLimitMb} MB`}
                  size="small"
                  color={
                    clusterStatus.thisServer.denoProcesses.totalMemoryMb >
                    clusterStatus.thisServer.denoProcesses.totalMemoryLimitMb
                      ? "error"
                      : "primary"
                  }
                />
                <Chip
                  label={`Processes: ${clusterStatus.thisServer.denoProcesses.processCount} / ${clusterStatus.thisServer.denoProcesses.maxProcesses}`}
                  size="small"
                />
                <Chip
                  label={`Matches: ${clusterStatus.thisServer.denoProcesses.totalMatches}`}
                  size="small"
                />
                <Chip
                  label={`Bundles: ${clusterStatus.thisServer.denoProcesses.bundlesLoaded}`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Per-process: target ${clusterStatus.thisServer.denoProcesses.targetMemoryMb} MB / cap ${clusterStatus.thisServer.denoProcesses.perProcessMemoryLimitMb} MB`}
                  size="small"
                  variant="outlined"
                />
              </Stack>

              {clusterStatus.thisServer.denoProcesses.processDetails.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No Deno processes running.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Process</TableCell>
                        <TableCell align="right">Memory</TableCell>
                        <TableCell align="right">Matches</TableCell>
                        <TableCell align="right">Connections</TableCell>
                        <TableCell align="right">Bundles</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clusterStatus.thisServer.denoProcesses.processDetails.map((proc) => {
                        const targetMb = clusterStatus.thisServer.denoProcesses.targetMemoryMb;
                        const capMb = clusterStatus.thisServer.denoProcesses.perProcessMemoryLimitMb;
                        const memColor: "error" | "warning" | "text.secondary" =
                          proc.memoryUsedMb >= capMb
                            ? "error"
                            : proc.memoryUsedMb >= targetMb
                              ? "warning"
                              : "text.secondary";
                        return (
                          <TableRow key={proc.processId}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                {proc.processId}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                sx={{
                                  color:
                                    memColor === "error" || memColor === "warning"
                                      ? `${memColor}.main`
                                      : "text.secondary",
                                }}
                              >
                                {proc.memoryUsedMb.toFixed(1)} MB
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{proc.matchCount}</TableCell>
                            <TableCell align="right">{proc.connectionCount}</TableCell>
                            <TableCell align="right">{proc.bundleCount}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Search Users
          </Typography>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <TextField
              label="Username"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  searchUsers();
                }
              }}
              size="small"
              sx={{ flex: 1 }}
              placeholder="Search by username"
            />
            <Button
              variant="contained"
              onClick={searchUsers}
              disabled={loading || !searchQuery.trim()}
            >
              {loading ? <CircularProgress size={24} /> : "Search"}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {searchResults.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Search Results
            </Typography>
            <Stack spacing={1}>
              {searchResults.map((user) => (
                <Box
                  key={user.id}
                  sx={{
                    p: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                  onClick={() => selectUser(user)}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    {user.avatarUrl && (
                      <Box
                        component="img"
                        src={user.avatarUrl}
                        alt="User avatar"
                        sx={{ width: 32, height: 32, borderRadius: "50%" }}
                      />
                    )}
                    <Box>
                      <Typography variant="body1">
                        {user.displayName || user.username}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        @{user.username}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {userInfo && (
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                {userInfo.avatarUrl && (
                  <Box
                    component="img"
                    src={userInfo.avatarUrl}
                    alt="User avatar"
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                    }}
                  />
                )}
                <Box>
                  <Typography variant="h6">
                    {userInfo.displayName || userInfo.username || "No display name"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {userInfo.id}
                  </Typography>
                  {userInfo.username && (
                    <Typography variant="body2" color="text.secondary">
                      @{userInfo.username}
                    </Typography>
                  )}
                </Box>
              </Stack>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Current Permissions
                </Typography>
                <Stack direction="row" spacing={1}>
                  {userInfo.role === "superuser" && (
                    <Chip label="Superadmin" color="error" size="small" />
                  )}
                  {userInfo.canCreateGames && (
                    <Chip label="Can Create Games" color="primary" size="small" />
                  )}
                  {!userInfo.role &&
                    !userInfo.canCreateGames && (
                      <Typography variant="body2" color="text.secondary">
                        No special permissions
                      </Typography>
                    )}
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Manage Permissions
                </Typography>
                <Stack direction="row" spacing={2}>
                  {userInfo.role === "superuser" ? (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => toggleSuperadmin(false)}
                      disabled={loading}
                    >
                      Remove Superadmin
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => toggleSuperadmin(true)}
                      disabled={loading}
                    >
                      Grant Superadmin
                    </Button>
                  )}

                  {userInfo.canCreateGames ? (
                    <Button
                      variant="outlined"
                      onClick={() => toggleCanCreateGames(false)}
                      disabled={loading}
                    >
                      Remove Game Creation
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={() => toggleCanCreateGames(true)}
                      disabled={loading}
                    >
                      Grant Game Creation
                    </Button>
                  )}
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
